import { DropdownMenu } from "radix-ui";
import type { ReactElement } from "react";
import { MenuEntries } from "./MenuEntries.tsx";
import type { MenuEntry } from "./types.ts";

export type MenuProps = {
  /** Items (`disabled`, `selected` = checkmark, `destructive`) and `{ id, separator: true }` dividers. */
  items: MenuEntry[];
  /** Trigger: a single Button / IconButton / Pressable. */
  children: ReactElement;
  align?: "start" | "end";
  /** Which side of the trigger it opens on (default below). A rail button at the window's bottom: side "right", align "end". */
  side?: "top" | "right" | "bottom" | "left";
  /** A caption under the items (an app version), not selectable. */
  footer?: string;
  /** Controlled open state (absent = the menu opens and closes itself from its trigger). */
  open?: boolean;
  /** The menu wants to open or close (trigger press, item chosen, Escape, outside press). */
  onOpenChange?: (open: boolean) => void;
  testID?: string;
};

export function Menu({ items, children, align = "start", side = "bottom", footer, open, onOpenChange, testID }: MenuProps) {
  return (
    <DropdownMenu.Root modal={false} open={open} onOpenChange={onOpenChange}>
      <DropdownMenu.Trigger asChild>{children}</DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="b-menu" align={align} side={side} sideOffset={6} collisionPadding={8} data-testid={testID}>
          <MenuEntries kind="dropdown" entries={items} />
          {footer === undefined ? null : (
            <div className="b-menu-footer" data-testid={testID === undefined ? undefined : `${testID}-footer`}>
              {footer}
            </div>
          )}
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}

export type PointMenuProps = {
  /** Items, as `Menu`. */
  items: MenuEntry[];
  /** Where it opens (viewport px, e.g. a right click); absent = closed. */
  at: { x: number; y: number } | undefined;
  onClose: () => void;
  testID?: string;
};

/**
 * A menu opened at a point instead of from a trigger: the context menu of a surface that handles right clicks itself
 * (the Sheet grid). Native: iOS `UIContextMenuInteraction` / `.contextMenu` on the cell, Android a `DropdownMenu`
 * with an `offset` at the press position.
 */
export function PointMenu({ items, at, onClose, testID }: PointMenuProps) {
  return (
    <DropdownMenu.Root modal={false} open={at !== undefined} onOpenChange={(o) => (o ? undefined : onClose())}>
      <DropdownMenu.Trigger asChild>
        <span aria-hidden className="b-point-menu-anchor" style={{ left: at?.x ?? 0, top: at?.y ?? 0 }} />
      </DropdownMenu.Trigger>
      <DropdownMenu.Portal>
        <DropdownMenu.Content className="b-menu" align="start" side="bottom" sideOffset={2} data-testid={testID}>
          <MenuEntries kind="dropdown" entries={items} />
        </DropdownMenu.Content>
      </DropdownMenu.Portal>
    </DropdownMenu.Root>
  );
}
